/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { Connections } from '../connections/connections.class';
import { Countertop } from '../countertops/countertop.class';
import { obstructionData } from './obstruction.data';
import { ObstructionType } from './enums';
import { Panel } from '../panels/panel.class';
import { PanelTrims } from '../panel-trims/panel-trims.class';
import { Publish } from '../../decorators';
import { SurroundMaterial } from '../surround-material/surround-material.class';

import { Axis, Vertex3 } from '../../spatial-classes';
import { getBaseMsrpCfn, getCenterCfn, getCenterOffsetsCfn, getIslandDescriptionCfn, getIslandSizeCfn, getItemizedMsrpCfn, getOverallSizeCfn, getSkuSizeDetailsCfn, isGrillIslandCfn, isLeftOverlapCfn, isRightOverlapCfn, rotateIslandCfn, setSizeStandardPanelsCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

import type { PanelData } from '../panels/types';
import type { ReferenceMap } from '../shared.types';
import type { CornerSize, CornerSizeIn, IslandAssetProperties, ItemizedIslandMsrp, ObstructionDataIn, ObstructionDataOut } from './types';
import type { Size, SizeIn } from '../../types';

export class Obstruction extends KitchenDesignBase<ObstructionDataIn> {
	#size: Size | CornerSize;
	#sku: string;
	#sizeLocked: boolean;
	#surround: SurroundMaterial | null;
	#spatialDirty: boolean;
	#axis: Axis;

	readonly type: ObstructionType;
	readonly connections: Connections;
	readonly panel: Panel;
	readonly panelTrims: PanelTrims;

	constructor (options:KitchenDesignOptions<ObstructionDataIn> = {}) {
		super(BlueprintInstanceType.Obstruction, options);

		const data = obstructionData(options.dataIn)

		this._id = data.id;
		this.#sku = 'obstruction';

		// initialize as false
		this.#sizeLocked = false;

		this.#axis = new Axis({ dataIn: data.axis });

		this.type = data.type;
		this.#size = { height: 0, width: 0, depth: 0 };

		this.connections = new Connections({ dataIn: data.connections, parent: this });
		this.panel = new Panel({ dataIn: data.panel ?? { size: data.size } as Partial<PanelData>, parent: this });
		this.panelTrims = new PanelTrims({ dataIn: data.trims, parent: this });
		this.#surround = data.surround
			? new SurroundMaterial({ dataIn: data.surround, parent: this })
			: null;

		this.#size = { ...this.#size, ...(data.size as Size) };

		this.setInitialized();

		this.refMap.kitchen?.subscribe('surround_material', 'updated', () => this.surroundMaterialUpdated());
	}

	/**
	 * obstruction origin represents the world space position of the obstruction
	 * For default obstruction: origin = center = 0,0,0 (world space)
	 * For other obstructions: origin = defaultIsland.origin + this.center
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		const fn = (t: Obstruction): Vertex3 => {
			const defaultIsland = t.refMap.obstructions?.getDefaultIsland();
			if (defaultIsland && t.id === defaultIsland.id) {
				return new Vertex3();
			}

			const origin = new Vertex3();
			if (defaultIsland) {
				origin.moveTo(defaultIsland.origin);
				origin.add(new Vertex3({ dataIn: { x: t.center.x, y: 0, z: t.center.z }}));
			}

			return origin;
		};

		return fn(this);
	}

	/**
	 * obstruction dimensions can't be edited
	 */
	get sizeLocked (): boolean { return this.#sizeLocked }
	get sku (): string { return this.#sku }
	get hasVents (): boolean { return this.panel?.hasVents ?? false }

	/**
	 * MSRP calculations based on obstruction type and size
	 */
	get msrpBase (): number { return getBaseMsrpCfn(this, this.#sku) }
	get msrpItemized (): ItemizedIslandMsrp { return getItemizedMsrpCfn(this) }

	/**
	 * Sets the obstruction size, includes updating panel sizes
	 * @emits island_size.updated
	 */
	@Publish('island_size', 'updated')
	set size (size: SizeIn | CornerSizeIn) {
		const baseSize = this.#size as Size;
		if (!this.#sizeLocked) this.#size = { ...baseSize, ...(size as SizeIn) };
		setSizeStandardPanelsCfn(this);
	}
	get size (): Size | CornerSize { return this.#size }

	get isCornerIsland (): boolean { return false }

	/**
	 * Returns true if the obstruction is the default obstruction, null if parent is not set
	 * @returns boolean
	 */
	get isDefaultIsland (): boolean {
		if ((this.refMap.obstructions ?? null) === null) return false;
		return this.id === this.refMap.obstructions?.getDefaultIsland()?.id;
	}

	get isGrillIsland (): boolean { return isGrillIslandCfn(this) }
	get isLeftOverlap (): boolean { return isLeftOverlapCfn(this) }
	get isRightOverlap (): boolean { return isRightOverlapCfn(this) }
	get description (): string { return getIslandDescriptionCfn(this, this.getSkuSizeDetails()) }
	get overallSize (): Size {
		return getOverallSizeCfn(this);
	}
	get surround (): SurroundMaterial | null { return this.#surround ?? this.refMap.kitchen?.material?.surround ?? null; }

	/**
	 * calculates size string value for use in the sku, and pricing countertops
	 * @returns string
	 */
	get skuSize (): string { return this.getSkuSizeDetails()[0] }

	/**
	 * locates and returns a Panel instance for the front panel
	 * @returns Panel
	 */
	get frontPanel (): Panel {
		return this.panel;
	}

	/**
	 * Compatibility getter that provides an Island-like panels interface
	 * Obstructions have a single panel, but this wraps it in a compatible structure
	 * @returns Object with children array and helper methods
	 */
	get panels (): { children: Panel[]; getFrontPanel: () => Panel; findById: (id: string) => Panel | null; frontPanelId: string; surroundMaterial: SurroundMaterial | null } {
		const { panel } = this;
		// Guard against accessing panel before it's initialized during construction
		if (!panel) {
			return {
				children: [],
				getFrontPanel: () => null,
				findById: (_id: string) => null,
				frontPanelId: null,
				surroundMaterial: null
			};
		}
		return {
			children: [panel],
			getFrontPanel: () => panel,
			findById: (id: string) => (panel.id === id ? panel : null),
			frontPanelId: panel.id,
			surroundMaterial: this.surround
		};
	}

	/**
	 * Returns height, width and depth for the obstruction as calculated from panels
	 * @returns Size
	 */
	get obstructionSize (): Size {
		return getIslandSizeCfn(this);
	}

	/**
	 * Alias for obstructionSize to maintain compatibility with Panel class expectations
	 * @returns Size
	 */
	get islandSize (): Size {
		return this.obstructionSize;
	}

	/**
	 * obstruction center represents the position relative to the default obstruction
	 * For default obstruction: center = 0,0,0 (relative to itself)
	 * For other obstructions: center = spatial delta from default obstruction (0,0,0)
	 * The center is calculated based on connections to the source obstruction
	 * @returns Vertex3 representing the center position relative to the default obstruction
	 */
	get center (): Vertex3 {
		const center = getCenterCfn(this);
		return center;
	}

	public getCenterOffsets (angle = 0, includeCountertop = false): { front: number; left: number; back: number; right: number; } {
		return getCenterOffsetsCfn(this, angle, includeCountertop);
		this.#spatialDirty = false;
	}

	/**
	 * Get and returns the countertop instance for the obstruction
	 * @returns Countertop
	 */
	public getCountertop (): Countertop {
		return (this._refMap?.kitchen?.design?.countertops.filterByIslandId(this.id) ?? [null])[0];
	}

	@Publish('island_axis', 'updated')
	set axis (axis: Axis) {
		this.#spatialDirty = true;
		this.#axis = axis;
	}
	get axis (): Axis { return this.#axis; }

	/**
	 * Rotates the obstruction clockwise, includes updating panel connections
	 * @emits island_position.updated
	 */
	@Publish('island_position', 'updated')
	public rotateClockwise (): void {
		this.#spatialDirty = true;
		rotateIslandCfn(this as Obstruction, true);
	}

	/**
	 * Rotates the obstruction counter-clockwise, includes updating panel connections
	 * @returns void
	 * @emits island_position.updated
	 */
	@Publish('island_position', 'updated')
	public rotateCounterClockwise (): void {
		this.#spatialDirty = true;
		rotateIslandCfn(this as Obstruction, false);
	}

	public getReferenceMap (id: string): ReferenceMap {
		return super.getReferenceMap(id, ['connections', 'panel', 'panelTrims']);
	}

	/**
	 * Returns a tuple with sku details based on the size of the obstruction
	 * @returns [string, IslandAssetData]
	 */
	private getSkuSizeDetails (): [string, IslandAssetProperties] {
		return getSkuSizeDetailsCfn(this);
	}

	/**
	 * Serializes the obstruction data
	 * @returns ObstructionDataOut
	 */
	public serialize (): ObstructionDataOut {
		return {
			id: this._id,
			sku: this.#sku,
			type: this.type,
			size: this.#size as Size | CornerSize,
			axis: this.#axis.serialize(),
			connections: this.connections.serialize(),
			trims: this.panelTrims.serialize(),
			panel: this.panel.serialize(),
			surround: this.#surround?.serialize() ?? null
		};
	}

	private surroundMaterialUpdated (): void {
		setSizeStandardPanelsCfn(this);
	}
}
